/// <amd-module name="Wit/Common/WorkItemControls/NewItemCallout" />
import * as React from "react";
import { useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { usePageContext } from "VSS/Platform/Layout";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { Button } from "VSSUI/Button";
import { Callout } from "VSSUI/Callout";
import { Dropdown, IDropdownProps } from "VSSUI/Dropdown";
import { FocusZoneDirection } from "VSSUI/FocusZone";
import { IListBoxItem } from "VSSUI/ListBox";
import { IMenuItem } from "VSSUI/Menu";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { SplitButton } from "VSSUI/SplitButton";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { Location } from "VSSUI/Utilities/Position";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import * as Resources from "Wit/Common/WorkItemControls/Resources";
import { TypeIconSize, WorkItemTypeColorAndIcon, WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";

const directionLocalStorageKey = "backlogStackRankDirection";
const defaultWorkItemTypeLocalStorageKeyFormat = "new-item-callout-witType";

export interface INewItemCalloutProps {
    /**
     * Optionally constrain the positions that items can be added at
     */
    allowedPositions?: Position[];
    /**
     * Anchor element for the callout
     */
    anchor: HTMLElement;
    /**
     * List of work item types that can be created
     */
    colorAndIcons: WorkItemTypeColorAndIcon[];
    /**
     * Optional way to keep callout open after adding item
     */
    keepOpenAfterAddingItem?: boolean;
    /**
     * Optional custom key generator for caching work item type selections
     * Useful when the callout's work item type options may be changing due to teams changing, etc.
     */
    getLocalStorageKeyForWorkItemType?: () => string;
    /**
     * Fetcher to get default work item path if needed
     */
    getBacklogDefaultWorkItemTypeNameFetcher?: {
        service: {
            getBacklogDefaultWorkItemTypeName: (teamId?: string, bagklogLevelId?: string) => Promise<string | undefined>;
        };
        context?: { teamId: string; backlog: string };
    };
    /**
     * Callback when an item is added.
     */
    onAddItem?: (title: string, witType: string, direction: "top" | "bottom" | "selection") => void;
    /**
     * Callback when the callout is closed
     */
    onDismiss: () => void;
}

export type Position = "top" | "bottom" | "selection";

/**
 * Displays a callout for adding a new item on the board.
 */
export function NewItemCallout({
    allowedPositions,
    anchor,
    colorAndIcons,
    getLocalStorageKeyForWorkItemType,
    getBacklogDefaultWorkItemTypeNameFetcher,
    onAddItem,
    onDismiss,
    keepOpenAfterAddingItem
}: INewItemCalloutProps): JSX.Element | null {
    if (colorAndIcons.length === 0) {
        return null;
    }

    const [textObservable] = useObservable("");
    const pageContext = usePageContext();

    // Remove after FF WebPlatform.WorkItemTracking.NewSprintsBacklogsView is removed
    const isNewSprintsViewEnabled = isFeatureFlagEnabled(pageContext, FeatureFlags.NewSprintsBacklogsView, false);

    let selectionFromStorage = tryGetLocalStorageValue(directionLocalStorageKey) as Position | null;
    if (allowedPositions?.length && !allowedPositions.some(d => d === selectionFromStorage)) {
        selectionFromStorage = allowedPositions[0];
    }
    const onMenuClick = (menuItem: IMenuItem) => {
        setDirectionState(menuItem.id as Position);
        trySetLocalStorageValue(directionLocalStorageKey, menuItem.id);
    };
    const items = React.useMemo(() => {
        const itemsArray = [];
        if (!allowedPositions || allowedPositions.some(d => d === "top")) {
            itemsArray.push({ id: "top", text: Resources.AddToTop, onActivate: onMenuClick });
        }
        if (!allowedPositions || allowedPositions.some(d => d === "bottom")) {
            itemsArray.push({ id: "bottom", text: Resources.AddToBottom, onActivate: onMenuClick });
        }

        if (!allowedPositions || allowedPositions.some(d => d === "selection")) {
            itemsArray.push({ id: "selection", text: Resources.AddAtSelection, onActivate: onMenuClick });
        }

        return itemsArray;
    }, [allowedPositions]);

    const [directionState, setDirectionState] = React.useState<Position>(() => selectionFromStorage || "top");

    const workItemTypeStorageKey = getLocalStorageKeyForWorkItemType ? getLocalStorageKeyForWorkItemType() : defaultWorkItemTypeLocalStorageKeyFormat;

    // use the last selected type if it's in the list of options
    const lastSelectedTypeFromStorage = tryGetLocalStorageValue(workItemTypeStorageKey);
    const lastSelectedType = colorAndIcons.find(icon => icon.workItemTypeName === lastSelectedTypeFromStorage);

    const [selectedType, setSelectedType] = React.useState<string>();
    React.useEffect(() => {
        if (lastSelectedType) {
            setSelectedType(lastSelectedType.workItemTypeName);
        } else {
            if (getBacklogDefaultWorkItemTypeNameFetcher) {
                const { teamId, backlog } = getBacklogDefaultWorkItemTypeNameFetcher.context ?? {};
                getBacklogDefaultWorkItemTypeNameFetcher.service
                    .getBacklogDefaultWorkItemTypeName(teamId, backlog)
                    .then(defaultWorkItemType => {
                        setSelectedType(
                            (colorAndIcons.find(icon => icon.workItemTypeName === defaultWorkItemType) && defaultWorkItemType) ||
                                colorAndIcons[0].workItemTypeName
                        );
                    })
                    .catch(() => {
                        setSelectedType(colorAndIcons[0].workItemTypeName);
                    });
            } else {
                setSelectedType(colorAndIcons[0].workItemTypeName);
            }
        }
    }, []);

    const renderCallout = (children: React.ReactNode) => (
        <Callout
            anchorElement={anchor}
            anchorOrigin={{ horizontal: isNewSprintsViewEnabled ? Location.start : Location.end, vertical: Location.end }}
            className="new-item-callout"
            contentClassName="flex-row flex-center padding-8 subtle-border depth-8"
            escDismiss
            focuszoneProps={{
                circularNavigation: true,
                defaultActiveElement: ".new-item-name-textbox",
                direction: FocusZoneDirection.Horizontal,
                focusOnMount: true,
                handleTabKey: true
            }}
            lightDismiss
            onDismiss={onDismiss}
        >
            {children}
        </Callout>
    );

    if (!selectedType) {
        return renderCallout(<Spinner size={SpinnerSize.small} />);
    }

    const addItem = () => {
        onAddItem?.(textObservable.value, selectedType, directionState);
        if (!keepOpenAfterAddingItem) {
            onDismiss();
        }
        textObservable.value = "";
    };

    const onSelectType = (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<{}>) => {
        setSelectedType(item.text);
        trySetLocalStorageValue(workItemTypeStorageKey, item.text);
    };

    const buttonProps = {
        text: directionState === "top" ? Resources.AddToTop : directionState === "bottom" ? Resources.AddToBottom : Resources.AddAtSelection,
        onClick: addItem
    };

    return renderCallout(
        <>
            {colorAndIcons.length === 1 ? (
                <WorkItemTypeIcon className="margin-right-8" data={colorAndIcons[0]} size={TypeIconSize.large} />
            ) : (
                <WorkItemDropdown
                    className="margin-right-8"
                    colorAndIcons={colorAndIcons}
                    onSelect={onSelectType}
                    selectedType={selectedType}
                    width={150}
                />
            )}
            <TextField
                autoFocus
                className="new-item-name-textbox margin-right-8"
                ariaLabel={format(Resources.EnterNewItemNameAriaLabel, selectedType)}
                value={textObservable}
                onChange={(e, newValue) => (textObservable.value = newValue)}
                onKeyPress={e => e.key === "Enter" && addItem()}
            />
            {items.length > 1 ? (
                <SplitButton
                    primary
                    buttonProps={buttonProps}
                    menuButtonProps={{
                        ariaLabel: Resources.ChangeDestination,
                        contextualMenuProps: {
                            menuProps: {
                                id: "directionMenu",
                                items
                            }
                        }
                    }}
                />
            ) : (
                <Button primary {...buttonProps} />
            )}
        </>
    );
}

interface IWorkItemDropdownProps extends Pick<IDropdownProps, "className" | "onSelect" | "width"> {
    colorAndIcons: WorkItemTypeColorAndIcon[];
    selectedType: string;
}

export function WorkItemDropdown(props: IWorkItemDropdownProps): JSX.Element {
    const { colorAndIcons, selectedType, ...rest } = props;
    const items = colorAndIcons.map(
        colorAndIcon =>
            ({
                iconProps: { render: () => <WorkItemTypeIcon className="margin-horizontal-8" data={colorAndIcon} suppressTooltip /> },
                id: colorAndIcon.workItemTypeName,
                text: colorAndIcon.workItemTypeName
            } as IListBoxItem)
    );

    const selection = new DropdownSelection();
    const selectionIndex = items.findIndex(item => item.text === selectedType);
    if (selectionIndex > -1) {
        selection.select(selectionIndex);
    }

    return <Dropdown ariaLabel={Resources.NewWorkItemDropdownAriaLabel} items={items} selection={selection} {...rest} />;
}
